import type { UserAnnouncementRow } from "@/types/announcements";

function isPromo(row: UserAnnouncementRow): boolean {
  return row.kind === "promo";
}

/**
 * Normalize RPC rows so older backends (missing courses/promo fields) stay safe.
 */
export function normalizeAnnouncementRow(
  raw: Partial<UserAnnouncementRow> & Pick<UserAnnouncementRow, "id" | "title">,
): UserAnnouncementRow {
  const rank = raw.courses_page_rank;
  return {
    id: raw.id,
    title: raw.title,
    summary: raw.summary ?? null,
    link: raw.link ?? null,
    thumbnail_url: raw.thumbnail_url ?? null,
    published_at: raw.published_at ?? null,
    stream_id: raw.stream_id ?? null,
    stream_slug: raw.stream_slug ?? null,
    instructor_id: raw.instructor_id ?? null,
    instructor_name: raw.instructor_name ?? null,
    instructor_avatar: raw.instructor_avatar ?? null,
    is_read: Boolean(raw.is_read),
    has_unread_bell: Boolean(raw.has_unread_bell),
    cta_label: raw.cta_label ?? null,
    show_on_courses_page: Boolean(raw.show_on_courses_page),
    courses_page_rank:
      typeof rank === "number" && Number.isFinite(rank) ? rank : null,
    kind: raw.kind ?? null,
    banner_thumbnail_url: raw.banner_thumbnail_url ?? null,
  };
}

/**
 * Unread courses-page announcement with the lowest `courses_page_rank`.
 * Promos are excluded (they use the CTA banner, not this modal).
 */
export function pickCoursesPageAnnouncement(
  rows: UserAnnouncementRow[],
): UserAnnouncementRow | null {
  let best: UserAnnouncementRow | null = null;
  for (const row of rows) {
    if (isPromo(row)) continue;
    if (!row.show_on_courses_page || row.is_read) continue;
    if (row.courses_page_rank == null) continue;
    if (
      !best ||
      (best.courses_page_rank != null &&
        row.courses_page_rank < best.courses_page_rank)
    ) {
      best = row;
    }
  }
  return best;
}

/** Promo rows for join/upgrade banners — ignore `is_read` / 30-day feed rules on the client. */
export function pickPromoAnnouncements(
  rows: UserAnnouncementRow[],
): UserAnnouncementRow[] {
  return rows.filter(isPromo);
}
